// src/scenes/inspections/InspectionFormMobile.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Chip,
  Checkbox,
  FormControlLabel,
  Snackbar,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import RefreshIcon from "@mui/icons-material/Refresh";

/* ========= STRICT ENDPOINTS ========= */
const API_ROOT = "https://figazuresql.swancreate.com/api/swa";

/* ✅ Use SchoolMst (was Customers) */
const URL_CUSTOMERS = `${API_ROOT}/SchoolMst`;

/* Kept for compatibility (no longer used by the UI) */
const URL_LOCATIONS = `${API_ROOT}/Locations`; // ?CustomerID={id}
const URL_PRODUCTS = `${API_ROOT}/Products`; // ?LocationID={id}

const URL_INSPECTION_MST = `${API_ROOT}/InspectionMst`;
const URL_INSPECTION_QUESTIONS = `${API_ROOT}/InspectionQuestions`; // ?inspection_apiid={guid}
const URL_INSPECTION_RESULT_DETAIL = `${API_ROOT}/InspectionResultDetail`; // POST rows

// Media
const MEDIA_UPLOAD_URL = "https://media.swancreate.com/upload";
const MEDIA_FOLDER = "coolersolutions";
const MEDIA_RETRIEVE_BASE = "https://media.swancreate.com/images/coolersolutions";

// Email
const EMAIL_SEND_URL = "https://mailgun.swancreate.com/send";

// Brand
const CS_LOGO =
  "https://img1.wsimg.com/isteam/ip/4cebc7ff-4de3-42a4-b677-08831bcd5460/Cooler_Solutions_Centered_Transparent-5abad1a.png/:/rs=w:400,h:400,cg=true,m/cr=w:400,h:400/qt=q:95";

/* ========= utils ========= */
async function safeFetch(url, options = {}) {
  const res = await fetch(url, options);
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(
      `${options.method || "GET"} ${url} -> ${res.status} ${res.statusText}${
        t ? ` — ${t}` : ""
      }`
    );
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}
function normalizeType(t) {
  const s = (t || "").toLowerCase().trim();
  if (["yes/no", "yesno", "y/n", "boolean"].includes(s)) return "yesno";
  if (["pass/fail", "passfail", "p/f"].includes(s)) return "passfail";
  if (["number", "numeric", "value", "temp", "temperature"].includes(s))
    return "number";
  return "text";
}
async function uploadMediaAndGetUrl(file) {
  const fd = new FormData();
  fd.append("file", file, file.name);
  fd.append("folder", MEDIA_FOLDER);
  const res = await fetch(MEDIA_UPLOAD_URL, { method: "POST", body: fd });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(
      `Upload failed: ${res.status} ${res.statusText}${txt ? ` — ${txt}` : ""}`
    );
  }
  let url = "";
  try {
    const data = await res.json();
    const raw =
      data?.url ||
      data?.path ||
      data?.filename ||
      data?.fileName ||
      data?.Location ||
      data?.Key;
    if (raw)
      url = String(raw).startsWith("http")
        ? raw
        : `${MEDIA_RETRIEVE_BASE}/${String(raw).split("/").pop()}`;
  } catch {}
  if (!url) url = `${MEDIA_RETRIEVE_BASE}/${encodeURIComponent(file.name)}`;
  return url;
}
const yesStyles = {
  "&.Mui-selected": {
    bgcolor: "success.main",
    color: "success.contrastText",
    boxShadow: 2,
    "&:hover": { bgcolor: "success.dark" },
  },
};
const noStyles = {
  "&.Mui-selected": {
    bgcolor: "error.main",
    color: "error.contrastText",
    boxShadow: 2,
    "&:hover": { bgcolor: "error.dark" },
  },
};
const passStyles = yesStyles;
const failStyles = noStyles;
const tintedCardSx = {
  borderRadius: 2,
  p: 1,
  background:
    "linear-gradient(180deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.03) 100%)",
  "&.MuiCard-root": {
    backdropFilter: "saturate(1.2)",
  },
};

/* Helpers for email content */
function formatDate(dt = new Date()) {
  const d = new Date(dt);
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  const yr = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${yr}-${mo}-${da} ${hh}:${mm}`;
}
function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/* ========= page ========= */
export default function InspectionFormMobile() {
  // selections
  const [customerId, setCustomerId] = useState("");
  // Location/Product are no longer needed by the form, but we keep state for compatibility
  const [locationId] = useState("");
  const [productId] = useState("");
  const [inspectionApiId, setInspectionApiId] = useState("");

  // data
  const [customers, setCustomers] = useState([]);
  const [locations] = useState([]); // kept (unused)
  const [products] = useState([]); // kept (unused)
  const [inspections, setInspections] = useState([]);
  const [questions, setQuestions] = useState([]);

  // ui
  const [busy, setBusy] = useState({
    customers: false,
    locations: false,
    products: false,
    inspections: false,
    questions: false,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [imgBusy, setImgBusy] = useState({}); // per-question
  const [emailSnack, setEmailSnack] = useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // answers
  const [answers, setAnswers] = useState({});
  const fileInputsRef = useRef({});

  /* Load schools + inspections */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setBusy((p) => ({ ...p, customers: true, inspections: true }));
        const [c, i] = await Promise.all([
          safeFetch(URL_CUSTOMERS), // SchoolMst
          safeFetch(URL_INSPECTION_MST),
        ]);
        if (!alive) return;

        // Normalize SchoolMst to expected shape
        const raw = Array.isArray(c) ? c : [];
        const normalizedSchools = raw.map((s) => {
          const id =
            s.CustomerID ??
            s.SchoolID ??
            s.SchoolId ??
            s.SiteID ??
            s.LocationID ??
            s.id ??
            s.apiid;
          const name =
            s.CustomerName ??
            s.SchoolName ??
            s.SiteName ??
            s.LocationName ??
            s.Name ??
            s.Title ??
            "School";
          return {
            CustomerID: String(id),
            CustomerName: String(name),
            Email: s.Email || s.ContactEmail || s.PrincipalEmail || "",
            ...s,
          };
        });
        setCustomers(normalizedSchools);

        const mst = Array.isArray(i) ? i : [];
        setInspections(
          mst.map((m) => ({
            apiid: m.apiid,
            InspectionTitle: m.InspectionTitle,
            isActive:
              typeof m.isActive === "string"
                ? m.isActive === "true" || m.isActive === "1"
                : !!m.isActive,
          }))
        );
      } catch (e) {
        if (alive) setError(e.message || "Load failed.");
      } finally {
        if (alive)
          setBusy((p) => ({ ...p, customers: false, inspections: false }));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* Questions for selected inspection */
  useEffect(() => {
    setQuestions([]);
    setAnswers({});
    if (!inspectionApiId) return;
    let alive = true;
    (async () => {
      try {
        setBusy((p) => ({ ...p, questions: true }));
        const qs = await safeFetch(
          `${URL_INSPECTION_QUESTIONS}?inspection_apiid=${encodeURIComponent(
            inspectionApiId
          )}`
        );
        if (!alive) return;
        const arr = Array.isArray(qs) ? qs : [];
        const normalized = arr.map((q, idx) => ({
          order: idx + 1,
          apiid: q.apiid,
          inspection_apiid: q.inspection_apiid || inspectionApiId,
          Text: q.Text,
          Type: normalizeType(q.Type),
        }));
        setQuestions(normalized);
        const init = {};
        normalized.forEach(
          (q) =>
            (init[q.apiid] = {
              score: "",
              notes: "",
              ImageUrl: "",
              isReinspect: false,
            })
        );
        setAnswers(init);
      } catch (e) {
        if (alive) setError(e.message || "Load questions failed.");
      } finally {
        if (alive) setBusy((p) => ({ ...p, questions: false }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [inspectionApiId]);

  /* Selected school */
  const selectedCustomer = useMemo(
    () => customers.find((c) => String(c.CustomerID) === String(customerId)),
    [customers, customerId]
  );

  /* For site inspections, treat the school as the "location" too; no product. */
  const selectedLocation = useMemo(() => {
    if (!selectedCustomer) return null;
    return {
      LocationID: selectedCustomer.CustomerID,
      LocationName: selectedCustomer.CustomerName,
    };
  }, [selectedCustomer]);

  const selectedProduct = null; // not applicable

  /* Ready when school + inspection chosen */
  const ready = !!(selectedCustomer && inspectionApiId);

  function setAnswer(qid, patch) {
    setAnswers((prev) => ({ ...prev, [qid]: { ...prev[qid], ...patch } }));
  }

  async function onImagePick(qid, file) {
    if (!file) return;
    setImgBusy((p) => ({ ...p, [qid]: true }));
    try {
      const url = await uploadMediaAndGetUrl(file);
      setAnswer(qid, { ImageUrl: url });
    } catch (e) {
      setError(e.message || "Image upload failed.");
    } finally {
      setImgBusy((p) => ({ ...p, [qid]: false }));
    }
  }

  // ---------- EMAIL BUILDERS ----------
  function buildEmailHTML(session) {
    const {
      customer,
      location,
      product,
      inspectionTitle,
      when,
      rows, // [{qNum, text, score, notes, imageUrl, reinspect}]
    } = session;

    const header = `<table width="100%" cellpadding="0" cellspacing="0" style="font-family: Arial, Helvetica, sans-serif;background:#f6f9fc;padding:24px 0;">
      <tr>
        <td>
          <table width="620" align="center" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e6eef5;">
            <tr>
              <td style="background:linear-gradient(90deg,#1976d2 0%,#2e7d32 50%,#d32f2f 100%);padding:16px 20px;">
                <table width="100%">
                  <tr>
                    <td style="vertical-align:middle;">
                      <img src="${CS_LOGO}" alt="Cooler Solutions" height="48" style="display:block;border:0;">
                    </td>
                    <td align="right" style="color:#fff;font-size:14px;">
                      <strong>${escapeHtml(inspectionTitle || "Inspection")}</strong><br/>
                      <span style="opacity:.9">${escapeHtml(when)}</span>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>`;

    const meta = `<tr><td style="padding:16px 20px 0 20px">
      <table width="100%" style="font-size:14px;color:#0b2239;">
        <tr>
          <td style="padding:8px 12px;background:#f0f7ff;border-radius:8px">
            <strong>School:</strong> ${escapeHtml(customer?.CustomerName || "")}<br/>
            <strong>Site (same as school):</strong> ${escapeHtml(
              location?.LocationName || customer?.CustomerName || ""
            )} &nbsp; (#${escapeHtml(location?.LocationID || customer?.CustomerID || "")})<br/>
            <strong>Product:</strong> ${product?.ProductName ? escapeHtml(product.ProductName) : "N/A"}
          </td>
        </tr>
      </table>
    </td></tr>`;

    const rowsHtml = rows
      .map((r) => {
        const badgeColor =
          (r.score || "").toLowerCase() === "pass" ||
          (r.score || "").toLowerCase() === "yes"
            ? "#2e7d32"
            : (r.score || "").toLowerCase() === "fail" ||
              (r.score || "").toLowerCase() === "no"
            ? "#d32f2f"
            : "#1976d2";
        const reinspect = r.reinspect
          ? `<span style="background:#ffb020;color:#1b2b41;padding:2px 8px;border-radius:999px;font-size:11px;margin-left:8px;">Reinspect</span>`
          : "";
        const img = r.imageUrl
          ? `<a href="${r.imageUrl}" style="color:#1976d2;text-decoration:none" target="_blank" rel="noopener">View Photo</a>`
          : `<span style="opacity:.6">—</span>`;

        return `<tr>
          <td style="padding:10px 14px;border-bottom:1px solid #eef3f7;font-size:14px;">
            <div style="margin-bottom:6px;color:#0b2239;"><strong>${r.qNum}. ${escapeHtml(
          r.text
        )}</strong></div>
            <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
              <span style="background:${badgeColor};color:#fff;padding:2px 10px;border-radius:999px;font-size:12px;">${escapeHtml(
          r.score
        )}</span>
              ${reinspect}
              <span>${img}</span>
            </div>
            ${
              r.notes
                ? `<div style="margin-top:6px;color:#284b63;background:#f8fafc;border:1px dashed #e4eef6;padding:8px;border-radius:8px;"><em>Notes:</em> ${escapeHtml(
                    r.notes
                  )}</div>`
                : ""
            }
          </td>
        </tr>`;
      })
      .join("");

    const table = `<tr><td style="padding:16px 20px 20px 20px">
      <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e6eef5;border-radius:10px;overflow:hidden;">
        <tr style="background:#f3f8fe">
          <th align="left" style="padding:12px 14px;font-size:12px;color:#5a7184;text-transform:uppercase;letter-spacing:.4px;">Inspection Details</th>
        </tr>
        ${rowsHtml}
      </table>
    </td></tr>`;

    const footer = `<tr><td style="padding:12px 20px 18px 20px;color:#5a7184;font-size:12px;">
      © ${new Date().getFullYear()} Cooler Solutions — This message was generated automatically.
    </td></tr>
    </table>
    </td></tr></table>`;

    return header + meta + table + footer;
  }

  function buildEmailText(session) {
    const { customer, location, product, inspectionTitle, when, rows } = session;
    const lines = [];
    lines.push(`Cooler Solutions — ${inspectionTitle || "Inspection"}`);
    lines.push(when);
    lines.push("");
    lines.push(`School: ${customer?.CustomerName || ""}`);
    lines.push(
      `Site: ${location?.LocationName || customer?.CustomerName || ""} (#${
        location?.LocationID || customer?.CustomerID || ""
      })`
    );
    if (product?.ProductName) lines.push(`Product: ${product.ProductName}`);
    lines.push("");
    rows.forEach((r) => {
      lines.push(`${r.qNum}. ${r.text}`);
      lines.push(`  Answer: ${r.score}${r.reinspect ? "  [Reinspect]" : ""}`);
      if (r.notes) lines.push(`  Notes: ${r.notes}`);
      if (r.imageUrl) lines.push(`  Photo: ${r.imageUrl}`);
      lines.push("");
    });
    return lines.join("\n");
  }

  async function sendSummaryEmail({ rows }) {
    const toEmail =
      selectedCustomer?.Email ||
      selectedCustomer?.email ||
      selectedCustomer?.ContactEmail ||
      "";

    if (!toEmail) {
      setEmailSnack({
        open: true,
        sev: "warning",
        msg:
          "Inspection saved, but no email was found on this School record.",
      });
      return;
    }

    const inspectionTitle =
      inspections.find((i) => String(i.apiid) === String(inspectionApiId))
        ?.InspectionTitle || "Inspection";
    const when = formatDate();

    const session = {
      customer: selectedCustomer,
      location: selectedLocation, // same as school
      product: selectedProduct, // null
      inspectionTitle,
      when,
      rows,
    };

    const html = buildEmailHTML(session);
    const text = buildEmailText(session);
    const subject = `Inspection Summary — ${selectedCustomer.CustomerName} — ${when}`;

    try {
      await safeFetch(EMAIL_SEND_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "*/*" },
        body: JSON.stringify({ to: toEmail, subject, html, text }),
      });
      setEmailSnack({
        open: true,
        sev: "success",
        msg: `Summary email sent to ${toEmail}`,
      });
    } catch (e) {
      setEmailSnack({
        open: true,
        sev: "error",
        msg: `Email failed: ${e.message}`,
      });
    }
  }

  // ---------- SUBMIT ----------
  async function onSubmitAll() {
    setError("");
    if (!ready) {
      setError("Select School and Inspection first.");
      return;
    }
    // require a score for each question
    const missing = questions.filter(
      (q) => !(answers[q.apiid]?.score || "").toString().trim()
    );
    if (missing.length) {
      setError(`Answer all questions before submitting. Missing: ${missing.length}`);
      return;
    }

    // Build API payloads and also collect for the email
    const nowRowsForEmail = [];
    const payloads = questions.map((q, idx) => {
      const a = answers[q.apiid] || {};
      const one = {
        inspection_apiid: q.inspection_apiid,
        question_apiid: q.apiid,
        // Treat school as the "location/site" for site inspections:
        CustomerID: selectedCustomer.CustomerID,
        CustomerName: selectedCustomer.CustomerName,
        LocationID: selectedLocation.LocationID,
        LocationName: selectedLocation.LocationName,
        // Product not applicable:
        ProductID: null,
        ProductName: null,

        question_text: q.Text,
        score: (a.score || "").toString().trim(),
        notes: a.notes || null,
        ImageUrl: a.ImageUrl || null,
        isReinspect: a.isReinspect ? 1 : 0,
      };
      nowRowsForEmail.push({
        qNum: idx + 1,
        text: q.Text,
        score: (a.score || "").toString().trim(),
        notes: a.notes || "",
        imageUrl: a.ImageUrl || "",
        reinspect: !!a.isReinspect,
      });
      return one;
    });

    try {
      setSubmitting(true);
      await Promise.all(
        payloads.map((p) =>
          safeFetch(URL_INSPECTION_RESULT_DETAIL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(p),
          })
        )
      );

      // email (don't block UI)
      sendSummaryEmail({ rows: nowRowsForEmail });

      // reset answers
      const reset = {};
      questions.forEach(
        (q) =>
          (reset[q.apiid] = {
            score: "",
            notes: "",
            ImageUrl: "",
            isReinspect: false,
          })
      );
      setAnswers(reset);
    } catch (e) {
      setError(e.message || "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container maxWidth="sm" sx={{ p: 1 }}>
      {/* Gradient bar */}
      <Box
        sx={{
          height: 8,
          borderRadius: 999,
          background:
            "linear-gradient(90deg, #1976d2 0%, #2e7d32 50%, #d32f2f 100%)",
          mb: 1,
          boxShadow: 2,
        }}
      />
      <Card elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <CardHeader
          title="Mobile Inspection"
          subheader="Select a school and inspection, answer questions, attach photos, then submit."
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "& .MuiCardHeader-subheader": { color: "rgba(255,255,255,0.85)" },
            pb: 1,
          }}
        />
        <CardContent sx={{ pt: 2 }}>
          {error && (
            <Box mb={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {/* Filters — School + Inspection only */}
          <Stack spacing={1.25}>
            <FormControl fullWidth>
              <InputLabel id="cust-label">School</InputLabel>
              <Select
                labelId="cust-label"
                label="School"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                {busy.customers && (
                  <MenuItem value="" disabled>
                    Loading…
                  </MenuItem>
                )}
                {customers.map((c) => (
                  <MenuItem key={c.CustomerID} value={String(c.CustomerID)}>
                    {`${c.CustomerName} (#${c.CustomerID})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={busy.inspections}>
              <InputLabel id="insp-label">Inspection</InputLabel>
              <Select
                labelId="insp-label"
                label="Inspection"
                value={inspectionApiId}
                onChange={(e) => setInspectionApiId(e.target.value)}
              >
                {busy.inspections && (
                  <MenuItem value="" disabled>
                    Loading…
                  </MenuItem>
                )}
                {inspections
                  .filter((i) => i.isActive !== false)
                  .map((i) => (
                    <MenuItem key={i.apiid} value={i.apiid}>
                      {i.InspectionTitle}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Questions */}
          {ready ? (
            busy.questions ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography>Loading questions…</Typography>
              </Box>
            ) : questions.length === 0 ? (
              <Alert severity="info">No questions found for this inspection.</Alert>
            ) : (
              <Stack spacing={1.25}>
                {questions.map((q) => {
                  const val =
                    answers[q.apiid] || {
                      score: "",
                      notes: "",
                      ImageUrl: "",
                      isReinspect: false,
                    };
                  const imgUploading = !!imgBusy[q.apiid];
                  const chipColor =
                    q.Type === "yesno" || q.Type === "passfail"
                      ? "info"
                      : q.Type === "number"
                      ? "secondary"
                      : "default";

                  return (
                    <Card key={q.apiid} variant="outlined" sx={tintedCardSx}>
                      <CardContent sx={{ "&:last-child": { pb: 1 } }}>
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <Typography variant="subtitle1" fontWeight={800}>
                              {q.order}. {q.Text}
                            </Typography>
                            <Chip size="small" color={chipColor} label={q.Type.toUpperCase()} />
                          </Stack>

                          {/* Answer control */}
                          {q.Type === "yesno" && (
                            <ToggleButtonGroup
                              exclusive
                              size="medium"
                              value={val.score || ""}
                              onChange={(_, v) => setAnswer(q.apiid, { score: v || "" })}
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: 2,
                                boxShadow: 1,
                                "& .MuiToggleButton-root": {
                                  px: 2.25,
                                  py: 1,
                                  border: "none",
                                  mr: 1,
                                  borderRadius: 2,
                                  bgcolor: "background.paper",
                                  boxShadow: 0.5,
                                },
                              }}
                            >
                              <ToggleButton value="Yes" sx={yesStyles}>
                                Yes
                              </ToggleButton>
                              <ToggleButton value="No" sx={noStyles}>
                                No
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}

                          {q.Type === "passfail" && (
                            <ToggleButtonGroup
                              exclusive
                              size="medium"
                              value={val.score || ""}
                              onChange={(_, v) => setAnswer(q.apiid, { score: v || "" })}
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: 2,
                                boxShadow: 1,
                                "& .MuiToggleButton-root": {
                                  px: 2.25,
                                  py: 1,
                                  border: "none",
                                  mr: 1,
                                  borderRadius: 2,
                                  bgcolor: "background.paper",
                                  boxShadow: 0.5,
                                },
                              }}
                            >
                              <ToggleButton value="Pass" sx={passStyles}>
                                Pass
                              </ToggleButton>
                              <ToggleButton value="Fail" sx={failStyles}>
                                Fail
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}

                          {q.Type === "number" && (
                            <TextField
                              label="Value"
                              type="number"
                              inputMode="decimal"
                              value={val.score || ""}
                              onChange={(e) =>
                                setAnswer(q.apiid, { score: e.target.value })
                              }
                              size="medium"
                              sx={{ maxWidth: 220 }}
                            />
                          )}

                          {q.Type === "text" && (
                            <TextField
                              label="Answer"
                              value={val.score || ""}
                              onChange={(e) =>
                                setAnswer(q.apiid, { score: e.target.value })
                              }
                              size="medium"
                              fullWidth
                            />
                          )}

                          {/* Notes */}
                          <TextField
                            label="Notes (optional)"
                            value={val.notes || ""}
                            onChange={(e) => setAnswer(q.apiid, { notes: e.target.value })}
                            size="medium"
                            fullWidth
                            multiline
                            minRows={2}
                          />

                          {/* Reinspect flag */}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!val.isReinspect}
                                onChange={(e) =>
                                  setAnswer(q.apiid, {
                                    isReinspect: e.target.checked,
                                  })
                                }
                                sx={{
                                  "&.Mui-checked": { color: "warning.main" },
                                }}
                              />
                            }
                            label="Reinspect"
                          />

                          {/* Photo */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                              bgcolor: "background.paper",
                              borderRadius: 2,
                              p: 1,
                            }}
                          >
                            {val.ImageUrl ? (
                              <a
                                href={val.ImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <img
                                  src={val.ImageUrl}
                                  alt="attached"
                                  style={{
                                    width: 48,
                                    height: 48,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                    border: "1px solid rgba(0,0,0,0.1)",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    maxWidth: 180,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {val.ImageUrl.replace(/^https?:\/\//, "")}
                                </Typography>
                              </a>
                            ) : (
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                No photo
                              </Typography>
                            )}

                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              style={{ display: "none" }}
                              ref={(el) => {
                                if (el) fileInputsRef.current[q.apiid] = el;
                              }}
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                e.target.value = "";
                                await onImagePick(q.apiid, f);
                              }}
                            />
                            <Tooltip title="Take/Upload Photo">
                              <span>
                                <IconButton
                                  onClick={() =>
                                    fileInputsRef.current[q.apiid]?.click()
                                  }
                                  disabled={imgUploading}
                                  size="small"
                                  sx={{
                                    bgcolor: "secondary.main",
                                    color: "secondary.contrastText",
                                    "&:hover": { bgcolor: "secondary.dark" },
                                    boxShadow: 1,
                                  }}
                                >
                                  {imgUploading ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <PhotoCameraIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )
          ) : (
            <Alert severity="info">
              Select School and Inspection to begin.
            </Alert>
          )}

          {/* Submit */}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                const reset = {};
                questions.forEach(
                  (q) =>
                    (reset[q.apiid] = {
                      score: "",
                      notes: "",
                      ImageUrl: "",
                      isReinspect: false,
                    })
                );
                setAnswers(reset);
              }}
              disabled={!ready || submitting || busy.questions}
              sx={{ borderRadius: 2 }}
            >
              Clear Answers
            </Button>
            <Button
              variant="contained"
              onClick={onSubmitAll}
              disabled={!ready || submitting || busy.questions}
              sx={{
                borderRadius: 2,
                px: 2.5,
                bgcolor: "primary.main",
                boxShadow: 2,
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              {submitting ? "Submitting…" : "Submit Inspection"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Email snackbar feedback */}
      <Snackbar
        open={emailSnack.open}
        autoHideDuration={5000}
        onClose={() => setEmailSnack((s) => ({ ...s, open: false }))}
        message={emailSnack.msg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: {
            bgcolor:
              emailSnack.sev === "success"
                ? "success.main"
                : emailSnack.sev === "warning"
                ? "warning.main"
                : "error.main",
            color: "#fff",
          },
        }}
      />
    </Container>
  );
}
